import React, { useCallback, useState } from 'react';
import { Box, Button, Collapse, IconButton, Popover, TextField, Tooltip } from '@mui/material';
import { HexColorPicker } from 'react-colorful';

// Google Slides-style preset palette, laid out to match the screenshot grid:
// neutrals, vivid hues, then lighter/darker tone rows for each hue family.
const PRESET_COLORS = [
  '#000000',
  '#4d4d4d',
  '#666666',
  '#999999',
  '#b7b7b7',
  '#cccccc',
  '#d9d9d9',
  '#efefef',
  '#f3f3f3',
  '#ffffff',

  '#b30000',
  '#ff0000',
  '#ff9900',
  '#ffff00',
  '#00ff00',
  '#00d9e7',
  '#4a86e8',
  '#0000ff',
  '#9900ff',
  '#ff00ff',

  '#e6b8af',
  '#f4cccc',
  '#fce5cd',
  '#fff2cc',
  '#d9ead3',
  '#d0e0e3',
  '#c9daf8',
  '#cfe2f3',
  '#d9d2e9',
  '#ead1dc',

  '#dd7e6b',
  '#ea9999',
  '#f9cb9c',
  '#ffe599',
  '#b6d7a8',
  '#a2c4c9',
  '#a4c2f4',
  '#9fc5e8',
  '#b4a7d6',
  '#d5a6bd',

  '#cc4125',
  '#e06666',
  '#f6b26b',
  '#ffd966',
  '#93c47d',
  '#76a5af',
  '#6d9eeb',
  '#6fa8dc',
  '#8e7cc3',
  '#c27ba0',

  '#a61c00',
  '#cc0000',
  '#e69138',
  '#f1c232',
  '#6aa84f',
  '#45818e',
  '#3c78d8',
  '#3d85c6',
  '#674ea7',
  '#a64d79',

  '#85200c',
  '#990000',
  '#b45f06',
  '#bf9000',
  '#38761d',
  '#134f5c',
  '#1155cc',
  '#0b5394',
  '#351c75',
  '#741b47',
];

const MAX_RECENT = 5;
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

// Shared recent-color list so all pickers see the same history within a session
let recentColors: string[] = [];
const addRecentColor = (color: string) => {
  if (!HEX_RE.test(color) || PRESET_COLORS.includes(color)) return;
  recentColors = [color, ...recentColors.filter((c) => c !== color)].slice(0, MAX_RECENT);
};

export interface ToolbarColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label: string;
  icon: React.ReactNode;
  allowTransparent?: boolean;
  disabled?: boolean;
}

const ToolbarColorPicker: React.FC<ToolbarColorPickerProps> = ({
  value,
  onChange,
  label,
  icon,
  allowTransparent = false,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hexInput, setHexInput] = useState(value);
  const [showCustom, setShowCustom] = useState(false);

  const handleOpen = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      setAnchorEl(e.currentTarget);
      setHexInput(value);
      setShowCustom(false);
    },
    [disabled, value]
  );

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleSwatchClick = useCallback(
    (color: string) => {
      onChange(color);
      setHexInput(color);
      addRecentColor(color);
      handleClose();
    },
    [onChange, handleClose]
  );

  const handleHexChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let v = e.target.value;
      if (!v.startsWith('#')) v = '#' + v;
      setHexInput(v);
      if (HEX_RE.test(v)) {
        onChange(v);
        addRecentColor(v);
      }
    },
    [onChange]
  );

  const handleGradientChange = useCallback(
    (color: string) => {
      setHexInput(color);
      onChange(color);
    },
    [onChange]
  );

  const handleGradientChangeComplete = useCallback((color: string) => {
    addRecentColor(color);
  }, []);

  return (
    <>
      <Tooltip title={label}>
        <IconButton size="small" onClick={handleOpen} disabled={disabled}>
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            {icon}
            <Box
              sx={{
                position: 'absolute',
                bottom: -2,
                left: 2,
                right: 2,
                height: 4,
                borderRadius: 0.5,
                backgroundColor: value === 'transparent' ? undefined : value,
                border: value === 'transparent' ? '1px dashed #999' : '1px solid rgba(0,0,0,0.12)',
              }}
            />
          </Box>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, width: 220 }}>
          {/* Transparent option */}
          {allowTransparent && (
            <Box
              onClick={() => handleSwatchClick('transparent')}
              sx={{
                mb: 1,
                px: 1,
                py: 0.5,
                fontSize: 12,
                cursor: 'pointer',
                borderRadius: 0.5,
                border: '1px dashed',
                borderColor: value === 'transparent' ? 'primary.main' : 'divider',
                textAlign: 'center',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              No color
            </Box>
          )}

          {/* Swatches */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: '3px', mb: 1 }}>
            {PRESET_COLORS.map((color) => (
              <Box
                key={color}
                onClick={() => handleSwatchClick(color)}
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '2px',
                  backgroundColor: color,
                  border: '1px solid',
                  borderColor: color === '#ffffff' ? '#ccc' : color,
                  cursor: 'pointer',
                  outline: value === color ? '2px solid' : 'none',
                  outlineColor: 'primary.main',
                  outlineOffset: 1,
                  '&:hover': { transform: 'scale(1.2)' },
                }}
              />
            ))}
          </Box>

          {/* Recent colors */}
          {recentColors.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ fontSize: 11, color: 'text.secondary', mb: 0.5 }}>Recent</Box>
              <Box sx={{ display: 'flex', gap: '3px' }}>
                {recentColors.map((color) => (
                  <Box
                    key={color}
                    onClick={() => handleSwatchClick(color)}
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: '2px',
                      backgroundColor: color,
                      border: '1px solid rgba(0,0,0,0.12)',
                      cursor: 'pointer',
                      outline: value === color ? '2px solid' : 'none',
                      outlineColor: 'primary.main',
                      outlineOffset: 1,
                      '&:hover': { transform: 'scale(1.2)' },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Hex input */}
          <TextField
            size="small"
            label="Hex"
            value={hexInput}
            onChange={handleHexChange}
            slotProps={{
              htmlInput: { maxLength: 7, style: { fontFamily: 'monospace', fontSize: 13 } },
            }}
            fullWidth
            error={!HEX_RE.test(hexInput) && hexInput !== 'transparent'}
          />

          {/* Custom color picker (react-colorful) */}
          <Button
            size="small"
            onClick={() => setShowCustom((v) => !v)}
            sx={{ mt: 1, textTransform: 'none', fontSize: 12, width: '100%' }}
          >
            {showCustom ? 'Hide custom' : 'Custom color…'}
          </Button>
          <Collapse in={showCustom}>
            <Box sx={{ mt: 1, '& .react-colorful': { width: '100%', height: 150 } }}>
              <HexColorPicker
                color={HEX_RE.test(hexInput) ? hexInput : '#000000'}
                onChange={handleGradientChange}
                onMouseUp={() => handleGradientChangeComplete(hexInput)}
                onTouchEnd={() => handleGradientChangeComplete(hexInput)}
              />
            </Box>
          </Collapse>
        </Box>
      </Popover>
    </>
  );
};

export default ToolbarColorPicker;
